import React, { Suspense } from 'react';
import { Box, Grid, Typography, Paper, Button } from '@material-ui/core';
import { useParams } from 'react-router-dom';
import { withRouter } from '../../../../util/router-compatibility';
import { Loader } from '@orijinworks/frontend-commons';
import useLearningPlanDetailsHook from './useLearningPlanDetailsHook';
import LearningPlanBreadcrumbs from '../components/learning-plan-breadcrumbs/learning-plan-breadcrumbs';
import styles from './learning-plan-details.style';
import { Stack } from '@mui/material';
import { countRules } from '../utils/learning-plans.utils';
import { makeStyles } from '@material-ui/styles';

const LearningPlanDetails = () => {
  const { key } = useParams();
  const useStyles = makeStyles(styles);

  const classes = useStyles();
  const {
    learningPlan,
    pillars,
    loading,
    error,
    handleBackToList,
    handleConfigureRules
  } = useLearningPlanDetailsHook(key);

  const breadcrumbItems = [
    {
      label: 'Learning Plans',
      onClick: handleBackToList,
    },
    {
      label: learningPlan?.title || 'Learning Plan Details',
      isCurrent: true,
    },
  ];

  if (loading) {
    return (
      <Grid container component="div" className={classes.newGridContent}>
        <Loader isLoading={true} />
      </Grid>
    );
  }

  if (error || !learningPlan) {
    return (
      <Grid container component="div" className={classes.newGridContent}>
        <Box display="flex" flexDirection="column" gap={2} width="100%">
          <LearningPlanBreadcrumbs items={breadcrumbItems} />
          <Box className={classes.errorContainer}>
            <Typography variant="h6" color="error">
              {error || 'Learning plan not found'}
            </Typography>
          </Box>
        </Box>
      </Grid>
    );
  }

  return (
    <Suspense fallback={<Loader isLoading={true} />}>
      <Grid container component="div" className={classes.newGridContent}>
        <Grid container item>
          <LearningPlanBreadcrumbs items={breadcrumbItems} />
        </Grid>

        <Box className={classes.contentContainer}>
          <Box className={classes.headerSection}>
            <Box className={classes.titleContainer}>
              <Typography variant="h4" className={classes.title}>
                {learningPlan.title}
              </Typography>
              <Typography variant="h6" className={classes.key}>
                {learningPlan.key}
              </Typography>
              {learningPlan.description && (
                <Typography variant="body1" className={classes.description}>
                  {learningPlan.description}
                </Typography>
              )}
            </Box>
          </Box>

          <Box className={classes.pillarsSection}>
            <Typography variant="h5" className={classes.pillarsLabel}>
              Pillars
            </Typography>
            <Stack spacing={2} className={classes.pillarsStack}>
              {pillars && pillars.length > 0 ? (
                pillars.map((pillar) => (
                  <Paper key={pillar.id} elevation={2} className={classes.pillarCard}>
                    <Typography variant="h6" className={classes.pillarTitle}>
                      {pillar.title}
                    </Typography>
                    {pillar.description && (
                      <Typography variant="body2" className={classes.pillarDescription}>
                        {pillar.description}
                      </Typography>
                    )}
                    <Box className={classes.pillarInfo}>
                      <Typography variant="body2" className={classes.pillarInfoText}>
                        <Box component="span" className={classes.pillarInfoLabel}>
                          Tagged Content:
                        </Box>{' '}
                        18 Programs, 24 Courses
                      </Typography>
                      {countRules(pillar.criteria) > 0 ? (
                        <Typography variant="body2" className={classes.pillarInfoText}>
                          <Box component="span" className={classes.pillarInfoLabel}>
                            Rules:
                          </Box>{' '}
                          <Box component="span" className={classes.pillarInfoLink} onClick={() => {}}>
                            {countRules(pillar.criteria)} Configured
                          </Box>
                        </Typography>
                      ) : (
                        <Typography variant="body2" className={classes.pillarInfoText}>
                          <Box component="span" className={classes.pillarInfoLabel}>Rules:</Box>{' '}
                          <Box 
                            component="span" 
                            className={classes.pillarInfoLink}
                            onClick={() => {
                              handleConfigureRules(pillar.id);
                            }}
                          >
                            Configure rule
                          </Box>
                        </Typography>
                      )}
                    </Box>
                    <Box className={classes.pillarButtons}>
                      <Button
                        variant="contained"
                        color="primary"
                        className={classes.configureRulesButton}
                        onClick={() => {
                          handleConfigureRules(pillar.id);
                        }}
                      >
                        Configure Rules
                      </Button>
                      <Button variant="text" className={classes.tagContentButton} onClick={() => {}}>
                        Tag Content
                      </Button>
                    </Box>
                  </Paper>
                ))
              ) : (
                <Typography variant="body2" className={classes.noPillars}>
                  No pillars found for this learning plan.
                </Typography>
              )}
            </Stack>
          </Box>
        </Box>
      </Grid>
    </Suspense>
  );
};

export default withRouter(LearningPlanDetails);

